import { z } from 'zod'
import { ArchivableSchema } from '../archive'
import { WeekdaySchema } from '../closer'
import { AuditPropsSchema } from '../common'
import {
  AccountStatusIdSchema,
  CompanyIdSchema,
  isId,
  ProgramIdSchema,
  TeamIdSchema,
  UserIdSchema,
  WorkflowActionIdSchema,
  WorkflowStageIdSchema,
  type ProgramId,
} from '../ids'
import { RoleIdSchema } from '../role'
import { ProgramTaskSchema, UnsavedProgramTaskSchema } from './programTask'

export const isProgramId = isId(ProgramIdSchema)

export type WithProgramId = {
  programId: ProgramId
}

/**
 * AccountStatusId - When they create their first account with that status it will enroll them in the program
 * RoleId - When they are assigned to that role it will enroll them in the program
 * TeamId - When they join that team it will enroll them in the program
 * ProgramId - When they complete that program it will enroll them in the program
 * WorkflowStageId - When they create their first account with that workflow stage it will enroll them in the program
 * WorkflowActionId - When they perform that workflow action for the first time it will enroll them in the program
 * userCreated - When the user is created they will automatically be enrolled in the program
 */
export const ProgramTriggerSchema = z.union([
  AccountStatusIdSchema,
  RoleIdSchema,
  TeamIdSchema,
  ProgramIdSchema,
  WorkflowStageIdSchema,
  WorkflowActionIdSchema,
  z.literal('userCreated'),
])
export type ProgramTrigger = z.infer<typeof ProgramTriggerSchema>

export const ProgramAutoEnrollmentTriggerSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('stage'), stageId: z.union([AccountStatusIdSchema, WorkflowStageIdSchema]) }),
  z.object({ type: z.literal('action'), actionId: WorkflowActionIdSchema }),
  z.object({ type: z.literal('role'), roleId: RoleIdSchema }),
  z.object({ type: z.literal('team'), teamId: TeamIdSchema }),
  z.object({ type: z.literal('program'), programId: ProgramIdSchema }),
  z.object({ type: z.literal('userCreated') }),
])
export type ProgramAutoEnrollmentTrigger = z.infer<typeof ProgramAutoEnrollmentTriggerSchema>

export const ProgramScopeIdSchema = z.union([CompanyIdSchema, TeamIdSchema])
export type ProgramScopeId = z.infer<typeof ProgramScopeIdSchema>

export const ProgramStatusSchema = z.enum(['active', 'inactive'])
export type ProgramStatus = z.infer<typeof ProgramStatusSchema>

export const ProgramCompletionSchema = z.enum(['timeline', 'taskCompletion']).meta({
  description:
    'If timeline, a user in the program will be marked completed after the total days of the program. If taskCompletion, a user in the program will be marked complete when they have finished all their tasks',
})
export type ProgramCompletion = z.infer<typeof ProgramCompletionSchema>

export const UnsavedProgramSchema = z.object({
  name: z.string(),
  description: z.string().optional(),
  trigger: ProgramTriggerSchema.nullable().optional(),
  triggers: z.array(ProgramAutoEnrollmentTriggerSchema).optional(),
  teamIds: TeamIdSchema.array()
    .optional()
    .meta({ description: 'Enable the program for these teams, if empty the program will be enabled for all teams' }),
  tasks: z.array(UnsavedProgramTaskSchema),
  companyId: CompanyIdSchema.optional(),
  status: ProgramStatusSchema,
  totalDays: z.number().optional().describe('duration of program in days'),
  completion: ProgramCompletionSchema.optional(),
  startWeekday: WeekdaySchema.nullable().optional().meta({ description: 'The weekday the program should start on' }),
  fromStatusId: AccountStatusIdSchema.optional().meta({
    description: 'The stage recruit should be in to be enrolled in the program, applies only for recruiting',
  }),
  accountStatusId: AccountStatusIdSchema.optional().meta({
    description: 'The stage recruit should be moved to after completing the program, applies only for recruiting',
  }),
  workflowActionId: WorkflowActionIdSchema.optional().meta({
    description: 'When a user finishes the program, take this action on the account',
  }),
})
export type UnsavedProgram = z.infer<typeof UnsavedProgramSchema>

export const ProgramDataSchema = z.object({
  ...AuditPropsSchema.shape,
  ...ArchivableSchema.shape,
  ...UnsavedProgramSchema.shape,
  ownerId: UserIdSchema,
  programId: ProgramIdSchema,
  companyId: CompanyIdSchema,
  tasks: z.array(ProgramTaskSchema),
  archivedAt: z.number().optional().meta({ description: 'Epoch milliseconds when the program was archived' }),
  activatedAt: z
    .number()
    .optional()
    .meta({ description: 'Epoch milliseconds when the program most recently became active' }),
  pausedAt: z.number().optional().meta({ description: 'Epoch milliseconds when the program was most recently paused' }),
})
export type ProgramData = z.infer<typeof ProgramDataSchema>

export const TinyProgramSchema = ProgramDataSchema.pick({
  programId: true,
  name: true,
  ownerId: true,
  teamIds: true,
  totalDays: true,
  completion: true,
})
export type TinyProgram = z.infer<typeof TinyProgramSchema>
